/**
 * ManualTransferRouletteDrawer — config (admin) das ROLETAS de transferência.
 *
 * O administrador cria até 5 GRUPOS de roleta. Cada grupo tem um nome, a
 * configuração de distribuição (modo ponderada/fixo + pesos por membro, via
 * RouletteConfigForm — a mesma UI da roleta da tool nativa do agente) e a
 * permissão "permitir que membros usem". Cada grupo disponível vira um chip
 * nomeado no AI Workspace; clicar distribui o contato atual.
 *
 * Salva em user(admin).transfer_roulettes (lido por apply_manual_transfer_
 * roulette, que usa a engine smooth-WRR). Dados da equipe via useRouletteTeamData.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Shuffle, AlertCircle, Lock, Plus, Trash2 } from 'lucide-react';
import assignmentService from '../../services/global/assignmentService';
import RouletteConfigForm from '../roulette/RouletteConfigForm';
import useRouletteTeamData from '../../hooks/useRouletteTeamData';

const MAX_GROUPS = 5;

// Switch compacto da permissão "permitir que membros usem".
const Switch = ({ checked, onChange, colors }) => (
  <button
    type="button"
    onClick={onChange}
    role="switch"
    aria-checked={checked}
    style={{
      position: 'relative', width: 36, height: 20, padding: 0, border: 'none',
      borderRadius: 999, cursor: 'pointer', flexShrink: 0,
      backgroundColor: checked ? colors.accentPrimary : colors.bgTertiary,
      transition: 'background-color 0.15s ease',
    }}
  >
    <span style={{
      position: 'absolute', top: 2, left: checked ? 18 : 2,
      width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff',
      transition: 'left 0.15s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

const newConfig = () => ({ mode: 'round_robin', weights: {}, fixed_member_id: null });

const ManualTransferRouletteDrawer = ({ isOpen, onClose, colors }) => {
  const team = useRouletteTeamData(isOpen);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(true);
  // groups: [{ key, id, name, allowMembers, config:{mode,weights,fixed_member_id} }]
  const [groups, setGroups] = useState([]);
  const keyRef = useRef(1);
  const newKey = () => `g_${keyRef.current++}`;

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoadingGroups(true);
    setError(null);
    assignmentService.getTransferRoulettes()
      .then((rouRes) => {
        if (cancelled) return;
        setIsOwner(rouRes?.is_owner !== false);
        setGroups((rouRes?.data || []).map((g) => ({
          key: newKey(),
          id: g.id || null,
          name: g.name || '',
          allowMembers: !!g.allow_members,
          config: {
            mode: g.mode === 'fixed' ? 'fixed' : 'round_robin',
            weights: { ...(g.weights || {}) },
            fixed_member_id: g.fixed_member_id || null,
          },
        })));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[TransferRoulettes] load:', err);
          setError('Não foi possível carregar as roletas.');
        }
      })
      .finally(() => { if (!cancelled) setLoadingGroups(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const updateGroup = (key, patch) =>
    setGroups((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));

  const addGroup = () => {
    if (groups.length >= MAX_GROUPS) return;
    setGroups((prev) => [
      ...prev,
      { key: newKey(), id: null, name: '', allowMembers: false, config: newConfig() },
    ]);
  };

  const removeGroup = (key) =>
    setGroups((prev) => prev.filter((g) => g.key !== key));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = groups.map((g) => ({
        id: g.id || undefined,
        name: (g.name || '').trim(),
        allow_members: g.allowMembers,
        mode: g.config.mode,
        weights: g.config.weights || {},
        fixed_member_id: g.config.mode === 'fixed' ? g.config.fixed_member_id : null,
      }));
      await assignmentService.setTransferRoulettes(payload);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erro ao salvar. Verifique a configuração.');
      console.error('[TransferRoulettes] save:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const loading = loadingGroups || (isOwner && team.loading);
  const noCompany = isOwner && team.error?.kind === 'no_company';

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary, width: 560, maxWidth: '100%',
          height: '100%', display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`, flexShrink: 0,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Shuffle size={18} color={colors.accentPrimary} />
              Roletas de transferência
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: colors.textSecondary }}>
              Crie até {MAX_GROUPS} roletas. Cada uma vira um chip no AI Workspace
              para distribuir o contato entre os membros.
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: colors.textSecondary, padding: 8, borderRadius: 8,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '18px 20px',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          {loading ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: colors.textSecondary, fontSize: 13, padding: 12,
            }}>
              <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
              Carregando...
            </div>
          ) : !isOwner ? (
            <div style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12,
              backgroundColor: colors.bgSecondary, borderRadius: 8,
              color: colors.textSecondary, fontSize: 13, lineHeight: 1.45,
            }}>
              <Lock size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              As roletas de transferência são definidas pelo administrador da empresa.
            </div>
          ) : noCompany ? (
            <div style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12,
              backgroundColor: colors.bgSecondary, borderRadius: 8,
              color: colors.textSecondary, fontSize: 13, lineHeight: 1.45,
            }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              A roleta distribui contatos entre a equipe — disponível em Conta Empresa com membros.
            </div>
          ) : (
            <>
              {groups.length === 0 && (
                <div style={{
                  padding: 14, borderRadius: 10, textAlign: 'center',
                  border: `1px dashed ${colors.borderColor}`,
                  color: colors.textTertiary, fontSize: 13, lineHeight: 1.5,
                }}>
                  Nenhuma roleta criada ainda. Crie a primeira para distribuir
                  contatos entre a equipe pelo AI Workspace.
                </div>
              )}

              {groups.map((group, idx) => (
                <div
                  key={group.key}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 14,
                    padding: 14, borderRadius: 12,
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.borderColor}`,
                  }}
                >
                  {/* Nome + excluir */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: `${colors.accentPrimary}18`, color: colors.accentPrimary,
                    }}>
                      <Shuffle size={14} />
                    </span>
                    <input
                      type="text"
                      value={group.name}
                      onChange={(e) => updateGroup(group.key, { name: e.target.value })}
                      placeholder={`Roleta ${idx + 1} (ex.: Vendedores)`}
                      maxLength={60}
                      style={{
                        flex: 1, padding: '8px 10px', borderRadius: 8,
                        border: `1px solid ${colors.borderColor}`,
                        backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                        fontSize: 13.5, fontWeight: 600, outline: 'none',
                      }}
                    />
                    <button
                      onClick={() => removeGroup(group.key)}
                      title="Excluir roleta"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 32, height: 32, flexShrink: 0, cursor: 'pointer',
                        background: 'transparent', border: `1px solid ${colors.borderColor}`,
                        borderRadius: 8, color: '#DC2626',
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  {/* Config compartilhada (modo/pesos/fixo) */}
                  <RouletteConfigForm
                    value={group.config}
                    onChange={(cfg) => updateGroup(group.key, { config: cfg })}
                    members={team.members}
                    categories={team.categories}
                    memberAgents={team.memberAgents}
                    company={team.company}
                    colors={colors}
                    radioName={group.key}
                  />

                  {/* Permissão */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    paddingTop: 4, borderTop: `1px solid ${colors.borderColor}`,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                        Permitir que membros usem
                      </div>
                      <div style={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.4 }}>
                        {group.allowMembers
                          ? 'Qualquer membro pode acionar esta roleta no chat.'
                          : 'Somente o administrador vê e aciona esta roleta.'}
                      </div>
                    </div>
                    <Switch
                      checked={group.allowMembers}
                      onChange={() => updateGroup(group.key, { allowMembers: !group.allowMembers })}
                      colors={colors}
                    />
                  </div>
                </div>
              ))}

              {/* Criar grupo */}
              {groups.length < MAX_GROUPS ? (
                <button
                  onClick={addGroup}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                    border: `1px dashed ${colors.accentPrimary}77`,
                    background: `${colors.accentPrimary}0d`,
                    color: colors.accentPrimary, fontSize: 13, fontWeight: 600,
                  }}
                >
                  <Plus size={15} /> Criar roleta
                </button>
              ) : (
                <span style={{ fontSize: 12, color: colors.textTertiary, textAlign: 'center' }}>
                  Limite de {MAX_GROUPS} roletas atingido.
                </span>
              )}

              {error && (
                <div style={{
                  display: 'flex', gap: 8, alignItems: 'center', padding: 10,
                  backgroundColor: '#DC262615', color: '#DC2626', borderRadius: 8, fontSize: 12,
                }}>
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {isOwner && !noCompany && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`, flexShrink: 0,
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px', backgroundColor: 'transparent',
                border: `1px solid ${colors.borderColor}`, borderRadius: 8,
                color: colors.textSecondary, fontSize: 13, cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 18px', backgroundColor: colors.accentPrimary,
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer', opacity: saving || loading ? 0.7 : 1,
              }}
            >
              {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              Salvar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualTransferRouletteDrawer;
